import type { ProfileParams } from '@/api/schema'
import { Layout } from '@/app/components/Layout/Layout'
import { OnboardingType } from '@/app/const'
import { useBack } from '@/app/hooks/useBack'
import { useMedia } from '@/app/hooks/useMedia'
import { useNavigate } from '@/app/hooks/useNavigate'
import { DateView } from '@/app/pages/OnboardingPage/DateView/DateView'
import { LocationView } from '@/app/pages/OnboardingPage/LocationView/LocationView'
import { LoginView } from '@/app/pages/OnboardingPage/LoginView/LoginView'
import { useSession } from '@/app/state/session'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useLayoutEffect, useState } from 'preact/hooks'
import { useShallow } from 'zustand/shallow'

enum Step {
  Name,
  Location,
  Login,
}

export const OnboardingPage: FunctionalComponent<{
  type: OnboardingType
  onSuccess?: () => void
  onStart?: () => void
}> = props => {
  const isMobile = useMedia(1023)
  const route = useNavigate()
  const back = useBack('~/tools')
  const [step, setStep] = useState(Step.Name)
  const [draftProfile, setDraftProfile] = useState<ProfileParams | null>(null)

  const sessionProfile = useSession(useShallow(s => s.profile))
  const sessionsetPatch = useSession(s => s.patchProfile)

  useLayoutEffect(() => {
    if (props.type === OnboardingType.MatrixKarma) {
      metrikaGoal('start_matrix_karma')
    }
    else if (props.type === OnboardingType.MatrixLove) {
      metrikaGoal('start_matrix_love')
    }
    else if (props.type === OnboardingType.MatrixMoney) {
      metrikaGoal('start_matrix_money')
    }
  }, [props.type])

  if (props.type === OnboardingType.Login) {
    return (
      <Layout>
        <LoginView
          type={OnboardingType.Login}
          profile={draftProfile ?? undefined}
          onBack={back}
          onSuccess={() => {
            route(isMobile ? '~/tools' : '~/tools/natal', { replace: true })
          }}
        />
      </Layout>
    )
  }

  return (
    <Layout>
      {step === Step.Name
        ? (
          <DateView
            type={props.type}
            userName={sessionProfile?.userName}
            birthDate={sessionProfile?.birthDate}
            patchProfile={profile => {
              props.onStart?.()
              return sessionsetPatch({ profile })
            }}
            onSuccess={() => {
              if (
                props.type === OnboardingType.Matrix
                || props.type === OnboardingType.MatrixMoney
                || props.type === OnboardingType.MatrixLove
                || props.type === OnboardingType.MatrixKarma
                || props.type === OnboardingType.Tarot
              ) {
                props.onSuccess?.()
              }
              else {
                setStep(Step.Location)
              }
            }}
            onRateLimit={profile => {
              setDraftProfile(profile)
              setStep(Step.Login)
            }}
            onBack={back}
          />
        )
        : step === Step.Location
        ? (
          <LocationView
            type={props.type}
            birthTime={sessionProfile?.birthTime}
            location={sessionProfile?.location}
            patchProfile={profile => (
              sessionsetPatch({ profile })
                .then(() => {
                  props.onSuccess?.()
                })
            )}
            onBack={() => {
              setStep(Step.Name)
            }}
          />
        )
        : (
          <LoginView
            type={props.type}
            profile={draftProfile ?? undefined}
            onBack={() => {
              setStep(Step.Name)
            }}
            onSuccess={() => {
              props.onSuccess?.()
            }}
          />
        )}
    </Layout>
  )
}
